import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { debounce } from 'lodash'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { type IParamsSearchMessages, MessagesApi } from 'entities/Message'
import { Message } from 'entities/Message/model/Message'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Conversation } from 'entities/Conversation'
import { type IResultsMessagesFetch } from 'pages/conversations/pages/search/ui/Results/store/types'

type IResultsMessagesStoreConfig = {
  onCount: (value: number) => void
}

const MIX_LIMIT_TOP_RESULT = 8

export class ResultsMessagesStore {
  private _cancelRequest = new RequestCancelAxios()
  private _loading = false
  private _loadingMore = false
  private _total = 0
  private _messagesMap: Map<number, Message> = new Map()
  private _conversationsMap: Map<number, Conversation> = new Map()
  private _disposeCount: IReactionDisposer | null = null
  private _pagination: IParamsSearchMessages = {
    per_page: 10,
    page: 1,
  }
  private _debounceLoadMessages: ReturnType<typeof debounce>

  constructor(private _entities: IEntitiesStore, private _config: IResultsMessagesStoreConfig) {
    makeAutoObservable(this)

    this._debounceLoadMessages = debounce(this.loadMessages, 500)
  }

  reactionCount = () => {
    this._disposeCount?.()
    this._disposeCount = reaction(
      () => this.count,
      (value) => {
        this._config.onCount(value)
      }
    )
  }

  fetchMessages = (config: IResultsMessagesFetch) => {
    this._loading = true
    this._debounceLoadMessages(config)
  }

  loadMessages = async ({ search, more }: IResultsMessagesFetch) => {
    try {
      this._cancelRequest.init()

      runInAction(() => {
        if (more) this._loadingMore = true
        else this._loading = true
      })

      const params = this._pagination

      if (more) {
        params.page += 1
      }

      if (search) {
        this._messagesMap.clear()
        params.term = removeSpacesFromNumericString(search)
        params.page = 1
      }

      const { data } = await MessagesApi.getSearchMessages(params, {
        cancelToken: this._cancelRequest.token,
      })

      this._entities.contactsStore.addItems(data.contacts)

      data.conversations.forEach((item) => {
        this._conversationsMap.set(item.id, new Conversation(item, this._entities))
        this._entities.contactsStore.addItems(item.participants)
      })

      data.messages.forEach((item) => {
        runInAction(() => {
          this._messagesMap.set(
            item.id,
            new Message(
              {
                message: item,
              },
              this._entities
            )
          )
        })
      })

      runInAction(() => {
        this._total = data.total
        this._loadingMore = false
        this._loading = false
      })
    } catch (e) {
      if (!isCancel(e)) {
        runInAction(() => {
          this._loadingMore = false
          this._loading = false
        })
      }
      logger.error(e)
    }
  }

  init = () => {
    this.reactionCount()
  }

  dispose = () => {
    this._cancelRequest.reset()
    this._disposeCount?.()
    this._total = 0
    this._debounceLoadMessages?.cancel()
    this._loading = false
    this._loadingMore = false
    this._messagesMap.clear()
    this._conversationsMap.clear()
    this._pagination = {
      per_page: 10,
      page: 1,
    }
  }

  getConversationItem = (id: number | undefined) => {
    if (!id) return

    return this._conversationsMap.get(id)
  }

  get loading() {
    return this._loading
  }

  get loadingMore() {
    return this._loadingMore
  }

  get hasMore() {
    const loadedCount = this._pagination.page * this._pagination.per_page

    return loadedCount < this._total
  }

  get items() {
    const items = Array.from(this._messagesMap.values())

    if (items.length > MIX_LIMIT_TOP_RESULT) {
      return items.filter((item, index) => index + 1 <= MIX_LIMIT_TOP_RESULT)
    }

    return items
  }

  get itemsAll() {
    return Array.from(this._messagesMap.values())
  }

  get count() {
    return this._total
  }

  get hasItems() {
    return Boolean(this.count)
  }
}
